import { useMemo } from 'react';
import { BLOCK_MARGIN } from '../utils/gridConstants';

/**
 * Computes position props for each block based on grid dimensions.
 * Pre-computing these values enables simple shallow comparison in PlanBlock.
 *
 * @param {Array} blocks - Array of block objects
 * @param {Map} weekIndexMap - Map of week string to column index
 * @param {number} cellWidth - Width of each grid cell
 * @param {number} rowHeight - Height of each row
 * @param {number} cellHeight - Height of each cell
 * @returns {Map} Map of blockId to position props
 */
const useBlockPositions = (
  blocks,
  weekIndexMap,
  cellWidth,
  rowHeight,
  cellHeight,
) => useMemo(() => {
  const positions = new Map();

  blocks.forEach((block) => {
    const startCol = weekIndexMap.get(block.fromWeek);
    const endCol = weekIndexMap.get(block.toWeek);

    if (startCol === undefined || endCol === undefined) return;

    // Skip blocks with invalid date range (toWeek before fromWeek)
    if (endCol < startCol) return;

    const weekSpan = endCol - startCol + 1;

    positions.set(block.id, {
      startCol,
      endCol,
      weekSpan,
      x: startCol * cellWidth + BLOCK_MARGIN,
      y: (block.row - 1) * rowHeight + BLOCK_MARGIN,
      width: weekSpan * cellWidth - BLOCK_MARGIN * 2,
      height: cellHeight - BLOCK_MARGIN * 2,
    });
  });

  return positions;
}, [blocks, weekIndexMap, cellWidth, rowHeight, cellHeight]);

export default useBlockPositions;
